import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';

import type { MetricForm } from '../formUtils';
import { validateFilter } from '../MetricEventFilters/filterUtils';
import { MetricEventQueryBuilder } from '../MetricEventQueryBuilder';
import {
  combineEventFilters,
  eventFilterToQuery,
  hasContextKindConflict,
  queryToEventFilter,
  splitFilterByCategory,
} from '../MetricEventQueryBuilder/eventQueryConversion';

// Connects the metric form's `filters` field to the two-bar QueryBuilder (MetricEventQueryBuilder).
// The form stores a single structured MetricFilter while the bars speak query strings split by
// category, so this is the conversion boundary: it splits the stored filter to seed each bar
// (filter -> string via eventFilterToQuery) and merges edits back (tokens -> filter via
// queryToEventFilter + combineEventFilters). Mirrors TraceField for trace metrics.
interface EventQueryFieldProps {
  context: UseFormReturn<MetricForm>;
}

const FIELD_NAME = 'filters';

export const EventQueryField: React.FC<EventQueryFieldProps> = ({ context }) => {
  const {
    control,
    trigger,
    formState: { errors },
  } = context;

  // QueryBuilder is keyed on `defaultValue`, so it must stay stable during editing or it would
  // remount and discard parsed tokens. But the stored filter can arrive *after* mount when
  // CreateMetricModal applies deferred defaultValues from a network request. So we keep re-seeding
  // each bar from the stored filter until the user first edits *that* bar, then freeze it. Freezing
  // per bar (not both at once) matters: editing one bar must not stop deferred metric data from
  // seeding the other, untouched bar — otherwise the untouched bar's loaded filters get dropped.
  const filters = useWatch({ control, name: FIELD_NAME });
  const split = filters ? splitFilterByCategory(filters) : undefined;
  const contextStoredQuery = split?.contextFilter ? eventFilterToQuery(split.contextFilter) : undefined;
  const eventStoredQuery = split?.eventFilter ? eventFilterToQuery(split.eventFilter) : undefined;
  const contextEditedRef = useRef(false);
  const eventEditedRef = useRef(false);
  const [contextSeed, setContextSeed] = useState<string | undefined>(contextStoredQuery);
  const [eventSeed, setEventSeed] = useState<string | undefined>(eventStoredQuery);

  useEffect(() => {
    if (!contextEditedRef.current) {
      setContextSeed(contextStoredQuery);
    }
  }, [contextStoredQuery]);
  useEffect(() => {
    if (!eventEditedRef.current) {
      setEventSeed(eventStoredQuery);
    }
  }, [eventStoredQuery]);

  // A pinned context kind that conflicts with a context-attribute filter (see hasContextKindConflict)
  // is surfaced as a validation error so the create/edit form can't be saved while the conflict
  // message is showing. The conflict lives only in the live bar tokens (the `context` pin is never
  // persisted), so track it here and fold it into the field's validate via a ref read at validation
  // time. Pinning a conflicting kind drops the non-persisted pin, leaving the merged `filters` value
  // unchanged, so `field.onChange` alone won't re-run validation — the effect below re-triggers it
  // whenever the conflict toggles so `isValid` (and the disabled Save button) stays in sync.
  const [conflict, setConflict] = useState(false);
  const conflictRef = useRef(false);
  const didMountRef = useRef(false);

  useEffect(() => {
    // Skip the mount run (no conflict yet, nothing to revalidate); only re-validate on toggles.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    void trigger(FIELD_NAME);
  }, [conflict, trigger]);

  return (
    <Controller
      control={control}
      name={FIELD_NAME}
      rules={{ validate: (value) => !conflictRef.current && validateFilter(value) }}
      render={({ field }) => (
        <MetricEventQueryBuilder
          contextDefaultValue={contextSeed}
          eventDefaultValue={eventSeed}
          inputId={FIELD_NAME}
          errorMessage={errors.filters?.message}
          // Reverse conversion: each bar's tokens => MetricFilter, merged into one stored filter on
          // every change. Editing a bar freezes only that bar's seed so the untouched bar keeps
          // tracking deferred defaultValues without remounting the edited one.
          onChange={(bars) => {
            if (bars.editedBar === 'context') {
              contextEditedRef.current = true;
            } else {
              eventEditedRef.current = true;
            }
            const nextConflict = hasContextKindConflict(bars.contextTokens);
            conflictRef.current = nextConflict;
            setConflict(nextConflict);
            field.onChange(
              combineEventFilters(queryToEventFilter(bars.contextTokens), queryToEventFilter(bars.eventTokens)),
            );
          }}
        />
      )}
    />
  );
};
